const statusCode = error => error?.response?.status ?? error?.status ?? error?.statusCode

export const createAuthenticatedFetch = (fetcher, {getToken, refreshAccessToken, onSessionExpired, server = false}) => {
    let refreshPromise = null
    let sessionExpired = false

    const expireSession = async () => {
        if (sessionExpired) return
        sessionExpired = true
        await onSessionExpired()
    }

    const refresh = () => {
        if (!refreshPromise) {
            refreshPromise = Promise.resolve().then(refreshAccessToken).catch(async error => {
                if ([401, 403].includes(statusCode(error))) await expireSession()
                throw error
            }).finally(() => { refreshPromise = null })
        }
        return refreshPromise
    }

    const run = async (requestFetch, request, options) => {
        if (!server && refreshPromise) await refreshPromise
        const tokenUsed = getToken()
        if (tokenUsed) sessionExpired = false

        try {
            return await requestFetch(request, options)
        } catch (error) {
            if (server || statusCode(error) !== 401 || (sessionExpired && !getToken())) throw error

            // A late 401 can belong to a request sent before another request refreshed the token.
            if (!getToken() || getToken() === tokenUsed) await refresh()

            try {
                return await requestFetch(request, options)
            } catch (retryError) {
                if (statusCode(retryError) === 401) await expireSession()
                throw retryError
            }
        }
    }

    const wrap = current => {
        const api = (request, options) => run(current, request, options)
        api.raw = (request, options) => run(current.raw, request, options)
        api.create = (...args) => wrap(current.create(...args))
        api.native = current.native
        return api
    }

    return wrap(fetcher)
}
